/* ============================================================================
   Diaper Bag Video Carousel — single deferred asset
   Three self-guarded modules, each wired document-wide so one load covers every
   section instance on the page (backpack + duffle):
     1. Native video carousel  (playback, arrows, centring)  — original asset
     2. Instagram reel modal    (auto-height via postMessage + loading spinner)
     3. Sub-tab switcher         (genericized: no Liquid)
   ========================================================================== */


/* ======================================================================
   MODULE 1 — Native video carousel  (your original asset, unchanged)
   ====================================================================== */
(function () {
  'use strict';

  if (window.__nintaraVideoLoaded) return;
  window.__nintaraVideoLoaded = true;

  var instances = [];

  /* Page-wide, not per-instance: the hidden bag type's videos must pause too. */
  function allVideos() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-nbv-video]'));
  }

  function pauseOthers(except) {
    allVideos().forEach(function (v) {
      if (v === except || v.paused) return;
      v.pause();
    });
  }

  /* =========================================================
     One instance
     ========================================================= */
  function initRoot(root) {
    if (!root || root.__nbvInit) return;
    root.__nbvInit = true;

    var BAG      = root.getAttribute('data-bag-type') || '';
    var viewport = root.querySelector('[data-nbv-viewport]');
    var track    = root.querySelector('[data-nbv-track]');
    var prev     = root.querySelector('[data-nbv-prev]');
    var next     = root.querySelector('[data-nbv-next]');
    var cards    = Array.prototype.slice.call(root.querySelectorAll('[data-nbv-card]'));
    if (!track || !cards.length) return;

    /* ==========================================================
       1. Playback
       ========================================================== */

    /* No scroll-snap on this track (see the section's header comment), so a
       card can be sitting half off-screen when tapped — pull it in first. */
    function scrollCardIntoView(card) {
      if (!card) return;
      var target = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
      var max = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: Math.max(0, Math.min(target, max)), behavior: 'smooth' });
    }

    cards.forEach(function (card) {
      var video = card.querySelector('[data-nbv-video]');
      var play  = card.querySelector('[data-nbv-play]');
      if (!video) return;

      /* Catches every future play — including a native-controls resume after a
         manual pause — not just the first tap. */
      video.addEventListener('play', function () { pauseOthers(video); });

      function start() {
        pauseOthers(video);
        scrollCardIntoView(card);
        card.classList.add('is-playing');
        video.controls = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }

      if (play) play.addEventListener('click', function (e) { e.stopPropagation(); start(); });

      /* Tapping the video itself does the same, but only before controls
         appear — afterwards the tap belongs to the native controls. */
      video.addEventListener('click', function () {
        if (!video.controls) start();
      });
    });

    /* Out-of-viewport auto-pause only — nothing plays on entering view. */
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting && !en.target.paused) en.target.pause();
        });
      }, { threshold: 0.25 });

      cards.forEach(function (c) {
        var v = c.querySelector('[data-nbv-video]');
        if (v) io.observe(v);
      });
    }

    /* Flipping bag type only applies display:none, which does NOT pause a
       playing <video> — it would keep going, invisible, with sound. */
    document.addEventListener('nintara:bagchange', function (e) {
      if (!e.detail || e.detail.bagType === BAG) return;
      cards.forEach(function (c) {
        var v = c.querySelector('[data-nbv-video]');
        if (v && !v.paused) v.pause();
      });
    });

    /* ==========================================================
       2. Arrows
       ========================================================== */
    function step() {
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return cards[0].getBoundingClientRect().width + gap;
    }

    function refresh() {
      if (!viewport) return;
      var overflows = track.scrollWidth - track.clientWidth > 1;
      viewport.classList.toggle('has-overflow', overflows);
      if (!overflows) {
        if (prev) prev.disabled = true;
        if (next) next.disabled = true;
        return;
      }
      var max = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    }

    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left:  step(), behavior: 'smooth' }); });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; refresh(); });
    }, { passive: true });

    /* ==========================================================
       3. Centre the chosen card on first view
       scrollLeft is unreadable and unwritable while the element is
       display:none, so this has to wait until the instance is
       actually visible — hence the bagchange hook below.
       ========================================================== */
    var centred = false;

    function centreInitial() {
      if (centred) return;
      if (!track.clientWidth) return;               /* still hidden */

      var idx = parseInt(root.getAttribute('data-initial-card'), 10) - 1;
      if (isNaN(idx) || idx < 0) idx = 0;
      if (idx > cards.length - 1) idx = cards.length - 1;

      var card = cards[idx];
      var target = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
      var max = track.scrollWidth - track.clientWidth;
      track.scrollLeft = Math.max(0, Math.min(target, max));

      centred = true;
      refresh();
    }

    document.addEventListener('nintara:bagchange', function (e) {
      if (!e.detail || e.detail.bagType !== BAG) return;
      window.requestAnimationFrame(function () { centreInitial(); refresh(); });
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { centreInitial(); refresh(); }).observe(track);
    }

    centreInitial();
    refresh();

    instances.push({
      root: root,
      onResize: refresh,
      reset: function () { centred = false; centreInitial(); refresh(); }
    });
  }

  /* =========================================================
     Window listeners — attached once, fanned out
     ========================================================= */
  function prune() {
    instances = instances.filter(function (i) { return document.contains(i.root); });
  }

  window.addEventListener('resize', function () {
    prune();
    instances.forEach(function (i) { i.onResize(); });
  }, { passive: true });

  /* =========================================================
     Init
     ========================================================= */
  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nbv]')).forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  /* Theme editor re-render — the __nbvInit flag keeps already-wired roots safe,
     and the re-measure catches a card width edited in the customizer. */
  document.addEventListener('shopify:section:load', function () {
    initAll();
    prune();
    window.requestAnimationFrame(function () {
      instances.forEach(function (i) { i.reset(); });
    });
  });
})();


/* ======================================================================
   MODULE 2 — Instagram reel modal

   A tap on an IG tile opens the per-section auto-height modal (instead of
   squeezing the reel into the 9:16 tile). IG's /embed/ iframe posts its true
   height via postMessage ({type:'MEASURE', details:{height}}); we match the
   iframe height to it, so the modal hugs the content with no black gap.

   ── LOADING SPINNER ──
   An IG embed takes a visible beat to fetch and paint, and until then the sheet
   is a black rectangle — the tap reads as a dead click. .is-loading goes on the
   modal the instant it opens and comes off on THREE signals, whichever lands
   first:
     1. MEASURE   — IG's own render signal, and the one that normally fires.
     2. iframe load + a short grace — covers a reel that never posts MEASURE
        (the contract is undocumented and has changed before). The grace is so
        the spinner doesn't lift onto a half-painted embed.
     3. a hard timeout — the spinner can never stick on a failed embed, which
        would look worse than the black box it replaced.
   Every timer is cleared in closeModal(), or a stale one would clear the class
   on the NEXT reel the shopper opens.

   ── CLOSING ──
   The X is now a sibling of .nbv__ig-modal-inner rather than a child of it (the
   inner box scrolls, and a scroll container clips anything above its top edge —
   that is what made the button invisible). Nothing here changed for it: the
   close listener still finds it with modal.querySelector, and the backdrop test
   is `e.target === modal`, which the button's own click never satisfies.
   ====================================================================== */
(function () {
  'use strict';

  if (window.__nbvIgLoaded) return;
  window.__nbvIgLoaded = true;

  var currentIframe = null;   /* the iframe currently in the open modal */
  var currentModal  = null;   /* the modal currently open */

  var loadGraceTimer = null;  /* iframe load -> short delay -> clear */
  var hardTimer      = null;  /* last-resort clear */

  var LOAD_GRACE_MS = 500;
  var HARD_STOP_MS  = 8000;

  function clearTimers() {
    if (loadGraceTimer) { clearTimeout(loadGraceTimer); loadGraceTimer = null; }
    if (hardTimer)      { clearTimeout(hardTimer);      hardTimer = null; }
  }

  function stopLoading() {
    clearTimers();
    if (currentModal) currentModal.classList.remove('is-loading');
  }

  /* IG's /embed/ iframe posts its true content height. Match the iframe height
     to it — this is what removes the black gap. Only instagram.com is trusted. */
  window.addEventListener('message', function (e) {
    if (!currentIframe) return;
    var host = '';
    try { host = new URL(e.origin).hostname; } catch (_) { return; }
    if (host.indexOf('instagram.com') === -1) return;
    var data = e.data;
    try { if (typeof data === 'string') data = JSON.parse(data); } catch (_) { return; }
    if (data && data.type === 'MEASURE' && data.details && data.details.height) {
      var h = parseInt(data.details.height, 10);
      if (h > 0) {
        currentIframe.style.height = h + 'px';
        stopLoading();                 /* IG has rendered -> drop the spinner */
      }
    }
  });

  function pauseAllVideos() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nbv-video]'))
      .forEach(function (v) { if (!v.paused) v.pause(); });
  }

  function closeModal() {
    if (!currentModal) return;
    clearTimers();                      /* or a stale timer un-spins the NEXT reel */
    var slot = currentModal.querySelector('[data-nbv-ig-iframe-slot]');
    if (slot) slot.innerHTML = '';      /* destroy iframe -> stops audio */
    currentModal.classList.remove('is-open', 'is-loading');
    currentIframe = null;
    currentModal = null;
    document.body.style.overflow = '';
  }

  function openModal(tile) {
    var url = tile.getAttribute('data-embed-url');
    if (!url) return;
    var section = tile.closest('.nbv');            /* this instance's section root */
    if (!section) return;
    var modal = section.querySelector('[data-nbv-ig-modal]');
    if (!modal) return;
    var slot  = modal.querySelector('[data-nbv-ig-iframe-slot]');
    var inner = modal.querySelector('.nbv__ig-modal-inner');
    if (!slot) return;

    closeModal();        /* one reel at a time, across the whole page */
    pauseAllVideos();    /* and stop any native video that's playing */

    var f = document.createElement('iframe');
    f.src = url;
    f.setAttribute('allow', 'autoplay; encrypted-media; clipboard-write; picture-in-picture; web-share');
    f.setAttribute('allowfullscreen', 'true');
    f.setAttribute('allowtransparency', 'true');
    f.setAttribute('scrolling', 'no');

    /* Fallback 2: the iframe document finished loading. Cross-origin still fires
       this; we just can't read inside. The grace lets IG paint before the
       spinner lifts, so there's no flash of a blank embed. */
    f.addEventListener('load', function () {
      if (loadGraceTimer) clearTimeout(loadGraceTimer);
      loadGraceTimer = setTimeout(stopLoading, LOAD_GRACE_MS);
    });

    slot.innerHTML = '';
    slot.appendChild(f);
    currentIframe = f;
    currentModal = modal;

    if (inner) inner.scrollTop = 0;

    /* Spinner on BEFORE the modal is shown, so its first painted frame already
       has it — otherwise there's a flash of the empty black sheet, which is the
       dead-click feel this is here to remove. */
    modal.classList.add('is-loading');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    /* Fallback 3 — a broken or blocked embed must not spin forever. */
    hardTimer = setTimeout(stopLoading, HARD_STOP_MS);
  }

  function wireTile(tile) {
    if (!tile || tile.__nbvIgInit) return;
    tile.__nbvIgInit = true;
    tile.addEventListener('click', function (e) {
      e.preventDefault();
      openModal(tile);
    });
  }

  function wireModal(modal) {
    if (!modal || modal.__nbvIgModalInit) return;
    modal.__nbvIgModalInit = true;
    var closeBtn = modal.querySelector('[data-nbv-ig-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {   /* click the dark backdrop to close */
      if (e.target === modal) closeModal();
    });
  }

  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nbv-ig]')).forEach(wireTile);
    Array.prototype.slice.call(document.querySelectorAll('[data-nbv-ig-modal]')).forEach(wireModal);
  }

  /* Escape closes the open reel. */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && currentModal) closeModal();
  });

  /* A native video starting -> close any open reel. */
  document.addEventListener('play', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-nbv-video]')) closeModal();
  }, true);

  /* Flipping bag type -> close any open reel. */
  document.addEventListener('nintara:bagchange', closeModal);

  /* Theme editor re-render. */
  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();


/* ======================================================================
   MODULE 3 — Sub-tab switcher
   Genericized from the inline version (which was scoped by {{ section.id }}):
   walks every .nbv section root, per-root guarded, re-inits in the editor.
   Behaviour unchanged.
   ====================================================================== */
(function () {
  'use strict';

  if (window.__nbvTabsLoaded) return;
  window.__nbvTabsLoaded = true;

  function wire(root) {
    if (!root || root.__nbvTabsInit) return;
    var tablist = root.querySelector('[data-nbv-tablist]');
    if (!tablist) return;                 /* no tabs on this instance — nothing to wire */
    root.__nbvTabsInit = true;

    var tabs  = Array.prototype.slice.call(root.querySelectorAll('[data-nbv-tab]'));
    var panes = Array.prototype.slice.call(root.querySelectorAll('[data-nbv-pane]'));

    function select(name) {
      tabs.forEach(function (t) {
        t.setAttribute('aria-selected', t.getAttribute('data-nbv-tab') === name ? 'true' : 'false');
      });
      panes.forEach(function (p) {
        var on = p.getAttribute('data-nbv-pane') === name;
        p.hidden = !on;
        if (!on) {
          Array.prototype.slice.call(p.querySelectorAll('video')).forEach(function (v) { if (!v.paused) v.pause(); });
        }
      });
      /* Revealed track went 0 -> real width; the shared asset's ResizeObserver re-centres it,
         and this refreshes the arrows via its window listener. */
      window.dispatchEvent(new Event('resize'));
    }

    tablist.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-nbv-tab]') : null;
      if (t) select(t.getAttribute('data-nbv-tab'));
    });
    tablist.addEventListener('keydown', function (e) {
      var t = e.target.closest ? e.target.closest('[data-nbv-tab]') : null;
      if (!t) return;
      var i = tabs.indexOf(t), n = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = tabs[(i + 1) % tabs.length];
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (e.key === 'Home') n = tabs[0];
      else if (e.key === 'End') n = tabs[tabs.length - 1];
      else return;
      e.preventDefault(); n.focus(); select(n.getAttribute('data-nbv-tab'));
    });
  }

  function initAll() {
    Array.prototype.slice.call(document.querySelectorAll('.nbv')).forEach(wire);
  }

  /* Theme editor re-render. */
  document.addEventListener('shopify:section:load', initAll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();